import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { showService } from '../services/endpoints/showService';
import { pedService } from '../services/endpoints/pedService';
import { planFileService } from '../services/endpoints/planFileService';
import ShowCountdown from '../components/show/ShowCountdown';
import CurrentStats from '../components/show/CurrentStats';
import LogFormModal from '../components/ped/LogFormModal';
import QuickCheckIn from '../components/dashboard/QuickCheckIn';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useCoachView } from '../contexts/CoachViewContext';
import AlertBanner from '../components/alerts/AlertBanner';
import { CoachPlanFile } from '../types';

const Dashboard: React.FC = () => {
  const [showLogDoseModal, setShowLogDoseModal] = useState(false);
  const queryClient = useQueryClient();

  // Coach read-only mode — driven by ?viewAs in the URL (via CoachViewContext)
  const { viewAs, isCoachView } = useCoachView();

  // Provision/fetch the current user profile (runs on every login)
  const { user } = useCurrentUser();

  const { data: compounds = [] } = useQuery({
    queryKey: ['ped-compounds'],
    queryFn: pedService.getCompounds,
  });

  const { data: dashboardData, isLoading, error } = useQuery({
    queryKey: ['dashboard', viewAs],
    queryFn: () => showService.getDashboardData(viewAs ?? undefined),
  });

  const { data: myPlanFiles = [] } = useQuery<CoachPlanFile[]>({
    queryKey: ['myPlanFiles'],
    queryFn: planFileService.listMine,
    enabled: !isCoachView,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-400">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-red-500">Error loading dashboard. Please try again.</div>
      </div>
    );
  }

  // Resolve first name: prefer the dashboard API value (sourced from DB),
  // fall back to the MSAL account name, then a generic greeting
  const firstName = dashboardData?.userFirstName || user?.firstName || '';
  const greeting = firstName ? `Hey ${firstName}! 👋` : 'Welcome back! 👋';

  if (!dashboardData?.activeShow) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <div className="text-center">
          {firstName && (
            <p className="text-gold-500 text-lg font-medium mb-2">{greeting}</p>
          )}
          <h2 className="text-2xl font-bold text-gray-300 mb-4">No Active Season</h2>
          <p className="text-gray-400 mb-8">Create a show to start tracking your prep season.</p>
          <a
            href="/shows"
            className="px-6 py-3 bg-gold-500 text-dark-900 font-semibold rounded-lg hover:bg-gold-400 transition-colors"
          >
            Create Show
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {/* Personalized Greeting Banner */}
      {firstName && !isCoachView && (
        <div className="px-8 pt-6 pb-2">
          <p className="text-gold-500 text-lg font-semibold tracking-wide">
            {greeting}
          </p>
        </div>
      )}

      {/* Alert Banner — shown when there are unacknowledged alerts */}
      {!isCoachView && (
        <div className="px-8 pt-2">
          <AlertBanner />
        </div>
      )}

      {/* Show Countdown Header */}
      <ShowCountdown
        show={dashboardData.primaryShow ?? dashboardData.activeShow}
        currentPhase={dashboardData.currentPhase}
        countdown={dashboardData.countdown}
        activeSeason={dashboardData.activeSeason}
      />

      {/* Current Stats */}
      <CurrentStats
        metrics={dashboardData.latestMetrics}
        cycle={dashboardData.activeCycle}
        phase={dashboardData.currentPhase}
        phaseWeek={dashboardData.countdown.phaseWeek}
      />

      {/* Quick Check-In — hidden in coach read-only view */}
      {!isCoachView && <QuickCheckIn />}

      {/* Quick Actions — hidden in coach read-only view */}
      {!isCoachView && (
        <div className="px-8 py-6">
          <h2 className="text-sm uppercase tracking-wider text-gray-400 mb-4">
            QUICK ACTIONS
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a
              href="/metrics/entry"
              className="p-6 bg-dark-700 rounded-lg border border-dark-700 hover:border-gold-500 transition-colors text-center"
            >
              <div className="text-3xl mb-2">📝</div>
              <div className="text-white font-semibold mb-1">Log Today's Check-In</div>
              <div className="text-sm text-gray-400">Weight, BP, vitals</div>
            </a>
            <button
              onClick={() => setShowLogDoseModal(true)}
              className="p-6 bg-dark-700 rounded-lg border border-dark-700 hover:border-gold-500 transition-colors text-center cursor-pointer"
            >
              <div className="text-3xl mb-2">💉</div>
              <div className="text-white font-semibold mb-1">Log PED Dose</div>
              <div className="text-sm text-gray-400">Track your cycle</div>
            </button>
            <a
              href="/analytics"
              className="p-6 bg-dark-700 rounded-lg border border-dark-700 hover:border-gold-500 transition-colors text-center"
            >
              <div className="text-3xl mb-2">📈</div>
              <div className="text-white font-semibold mb-1">View Progress</div>
              <div className="text-sm text-gray-400">Charts & trends</div>
            </a>
          </div>
        </div>
      )}

      {/* Plan Files — shown only when coach has shared files; hidden in coach view */}
      {!isCoachView && myPlanFiles.length > 0 && (
        <PlanFilesSection files={myPlanFiles} />
      )}

      {/* Log Dose Modal */}
      {!isCoachView && showLogDoseModal && (
        <LogFormModal
          compounds={compounds}
          activeCycle={dashboardData?.activeCycle}
          onClose={() => setShowLogDoseModal(false)}
          onSuccess={() => {
            setShowLogDoseModal(false);
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;

// ── Plan Files Section ────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(contentType: string): string {
  if (contentType === 'application/pdf') return '📄';
  if (contentType.includes('spreadsheet') || contentType.includes('excel')) return '📊';
  if (contentType.includes('word') || contentType.includes('msword')) return '📝';
  if (contentType.startsWith('image/')) return '🖼';
  return '📎';
}

const PlanFilesSection: React.FC<{ files: CoachPlanFile[] }> = ({ files }) => {
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async (file: CoachPlanFile) => {
    setDownloading(file.planFileId);
    setDownloadError(null);
    try {
      await planFileService.downloadFile(file.planFileId, file.originalFileName);
    } catch {
      setDownloadError('Failed to download file. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="px-8 py-6">
      <h2 className="text-sm uppercase tracking-wider text-gray-400 mb-4">PLAN FILES</h2>
      {downloadError && (
        <p className="text-red-400 text-sm mb-3">{downloadError}</p>
      )}
      <div className="bg-dark-800 rounded-xl border border-dark-700 divide-y divide-dark-700">
        {files.map(file => {
          const uploaded = new Date(file.uploadedAt).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
          });
          return (
            <div key={file.planFileId} className="flex items-center gap-4 px-5 py-4">
              <span className="text-2xl shrink-0">{fileIcon(file.contentType)}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate" title={file.originalFileName}>
                  {file.originalFileName}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  From {file.coachName} · {formatFileSize(file.fileSizeBytes)} · {uploaded}
                </p>
                {file.description && (
                  <p className="text-xs text-gray-400 mt-1 line-clamp-2">{file.description}</p>
                )}
              </div>
              <button
                onClick={() => handleDownload(file)}
                disabled={downloading === file.planFileId}
                className="shrink-0 px-3 py-1.5 text-xs text-blue-400 hover:text-blue-300 disabled:text-gray-500 border border-blue-400/30 hover:border-blue-300/30 disabled:border-dark-600 rounded-lg transition-colors"
              >
                {downloading === file.planFileId ? 'Downloading...' : 'Download'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
